/**
 * Материалы блога с бэкенда.
 *
 * Раньше список жил константой в коде сайта: добавленная в админке статья на
 * сайт не попадала, а те, что были на сайте, не видел менеджер. Теперь
 * источник один — база, а константы остались только для рубрик и оформления.
 *
 * Если ручка не ответила, показываем пустой список: выдумывать материалы,
 * которых нет, хуже, чем честно показать, что подборка не загрузилась.
 */

import type { BlogArticle, BlogCategoryId } from '@/constants/blog'

type ApiArticle = {
  id: number
  slug: string | null
  title: string
  cover: string | null
  category: BlogCategoryId
  tags?: { title: string, titleEn?: string | null, slug: string, color: string | null }[]
  excerpt: string | null
  body: string | null
  author: string | null
  authorReferrer?: boolean
  authorPhoto?: string | null
  publishedAt: string | null
  readingTime: number | null
  views: number
}

type ListResponse = { articles: ApiArticle[] }

/** Приводим к тому виду, в котором страницы блога уже умеют работать. */
const toArticle = (item: ApiArticle): BlogArticle & { excerpt: string | null; body: string | null } => ({
  id: item.id,
  /*
   * Адрес — транскрипция заголовка: /blog/skalping-na-moex вместо /blog/2.
   * У материалов, заведённых до перехода, он может быть пуст — тогда
   * открываем по номеру, как раньше, чтобы ссылка не сломалась.
   */
  slug: item.slug || String(item.id),
  title: item.title,
  cover: item.cover ?? null,
  category: item.category,
  // Порядок сохраняем: первый тег задаёт цвет обложки
  tags: item.tags ?? [],
  publishedAt: String(item.publishedAt ?? '').slice(0, 10),
  views: item.views ?? 0,
  readingTime: item.readingTime ?? 5,
  author: item.author ?? 'Команда АДФ',
  authorReferrer: Boolean(item.authorReferrer),
  authorPhoto: item.authorPhoto ?? null,
  excerpt: item.excerpt,
  body: item.body,
})

export const useBlogArticles = async () => {
  const { data, error } = await useApi<ListResponse>('blog/articles', {
    key: 'blog-articles',
    params: { pageSize: 200 },
  })

  if (error.value) {
    console.error('Блог: список материалов не загрузился', error.value)
  }

  const articles = computed(() => (data.value?.articles ?? []).map(toArticle))

  /*
   * Сбой загрузки отдаём отдельно от пустого списка: страница статьи должна
   * отличать «такой статьи нет» от «список не загрузился».
   */
  const failed = computed(() => Boolean(error.value))

  return { articles, failed }
}
